import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted, watch } from "vue";
  import store from "@/store";
  import router from "@/router";
  import {
    ImagesHouseTypes,
    ImageHouseLabels,
  } from "@/enums/BuilderHouseImages";
  import {
    Floorplan,
    Inclusions,
    HouseFeature,
    HouseDesignInterface,
  } from "@/types/HouseDesignInterface";
  import { ImageToRender } from "@/types/HouseCreateImageToRender";
  import { useToast } from "vue-toastification";
  import { required, minValue, maxValue } from "@vuelidate/validators";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import useVuelidate from "@vuelidate/core";
  import VuePictureCropper, { cropper } from "vue-picture-cropper";
  import ImageCropperModal from "@/components/ui/modals/ImageCropperModal.vue";
  import { ImageToUpload } from "@/types/BuilderImageToUpload";
  import {
    validImagesColorInputs,
    validsImagesNameInputs,
    validImagesDescriptionInputs,
    validsMultipleImagesInputs,
  } from "@/constants/adminHouseInputs";
  import ListItemGenerator from "@/components/builder/ListItemGenerator.vue";
  import {
    formatPrice,
    formatPriceTrim,
    updatePriceInput,
  } from "@/helpers/transformation/price";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import { v4 as uuidv4 } from "uuid";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import {
    ColorSchemeInterface,
    CreateInternalColorSchemeInterface,
    InternalColorSchemeInterface,
  } from "@/types/ColorSchemeInterface";
  import { ColorSchemeType } from "@/enums/ColorSchemeType";
  import {
    CreateInclusionInterface,
    InclusionInterface,
  } from "@/types/InclusionInterface";
  import {
    CreateOptionalUpgradeInterface,
    OptionalUpgrade,
  } from "@/types/OptionalUpgradeInterface";
  import { InclusionCategory } from "@/enums/InclusionCategory";
  import { AssetGalleryFormType } from "@/enums/AssetGalleryFormType";
  import AddAndUpdateAssetInGalleryModal from "@/components/ui/modals/AddAndUpdateAssetInGalleryModal.vue";
  import AddAndUpdateInclusionModal from "@/components/ui/modals/AddAndUpdateInclusionModal.vue";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminBuilderUpdateHouse',
  setup(__props, { expose: __expose }) {
  __expose();

  const houseId = computed(() => router.currentRoute.value.params.houseId);
  const imageToUpload = ref<ImageToUpload>({
    id: "",
    data: null,
    type: "" as ImagesHouseTypes,
  });
  const isCropperModalOpen = ref<boolean>(false);
  const toast = useToast();
  const filteredFacades = computed(() => {
    return facades.value.filter((facade) => facade.storeys === form.storey);
  });
  const facades = ref<FacadeInterface[]>([]);
  const changesStorey = ref(0);

  const internalColorSchemes = ref<InternalColorSchemeInterface[]>([]);
  const optionalUpgrades = ref<OptionalUpgrade[]>([]);

  const loading = ref(false);
  const isUploadingImage = ref(false);
  const isUploadingInclusion = ref(false);
  const inclusionName = ref("");
  const inclusions = ref<InclusionInterface[]>([]);
  const house = ref<HouseDesignInterface | null>(null);
  const houseFeatures = ref<HouseFeature[]>([]);
  const newFeature = ref("");
  const homeDesignUrl = ref("");
  const isInternalSchemeModalOpen = ref(false);
  const isInclusionModalOpen = ref(false);
  const isOptionalUpgradeModalOpen = ref(false);

  const getEmptyImage = () => {
    return { id: uuidv4(), url: "" };
  };

  const imagesToRender = ref<ImageToRender[]>([
    {
      type: ImagesHouseTypes.MAIN_IMAGE,
      data: [{ id: uuidv4(), url: "" }],
      label: ImageHouseLabels.MAIN_IMAGE,
    },
    {
      type: ImagesHouseTypes.FLOORPLANS,
      data: [],
      label: ImageHouseLabels.FLOORPLANS,
    },
  ]);

  const formFields = {
    facades: [] as string[],
    internalColorSchemes: [] as string[],
    floorplans: [] as Record<string, string>[],
    lounges: [] as Record<string, string>[],
    kitchens: [] as Record<string, string>[],
    bathrooms: [] as Record<string, string>[],
    inclusions: [] as Inclusions[],
    features: [] as string[],
    mainImages: [] as Record<string, string>[],
    name: "",
    description: "",
    area: null as number | null,
    length: null as number | null,
    width: null as number | null,
    bathroom: null as number | null,
    bedroom: null as number | null,
    garage: null as number | null,
    storey: null as number | null,
    loungeRoom: null as number | null,
    basePrice: 0 as number,
    inclusionPrices: {} as Record<string, number>,
    optionalUpgrades: [] as OptionalUpgrade[],
    categorizedInclusions: [] as {
      category: InclusionCategory;
      items: string[];
    }[],
  };

  const form = reactive(Object.assign({}, formFields));

  const inclusionCategories = [
    { key: InclusionCategory.INDOOR, label: "Indoor Inclusions" },
    { key: InclusionCategory.KITCHEN, label: "Kitchen" },
    { key: InclusionCategory.BATHROOM_LAUNDRY, label: "Bathroom & Laundry" },
    { key: InclusionCategory.OUTDOOR, label: "Outdoor" },
  ];

  const categorizedInclusionsMap = reactive<
    Record<InclusionCategory, string[]>
  >({
    [InclusionCategory.INDOOR]: [],
    [InclusionCategory.KITCHEN]: [],
    [InclusionCategory.BATHROOM_LAUNDRY]: [],
    [InclusionCategory.OUTDOOR]: [],
  });

  const rules = {
    facades: { required },
    internalColorSchemes: {},
    floorplans: { required },
    inclusions: {},
    features: {},
    lounges: {},
    kitchens: {},
    bathrooms: {},
    name: { required },
    area: { required, minValue: minValue(1) },
    length: { required, minValue: minValue(1) },
    width: { required, minValue: minValue(1) },
    bathroom: { required, minValue: minValue(1) },
    bedroom: { required, minValue: minValue(1) },
    garage: { required, minValue: minValue(1) },
    storey: { required, minValue: minValue(1), maxValue: maxValue(2) },
    loungeRoom: { required, minValue: minValue(1) },
    basePrice: {},
    mainImages: {},
    optionalUpgrades: {},
  };

  const v$ = useVuelidate(rules, form);

  const builderId = computed(() => {
    return router.currentRoute.value.params.builderId || house.value?.builderId;
  });

  const perSQM = computed(() => {
    return !form.basePrice || !form.area
      ? "$0"
      : `${formatPrice(form.basePrice / form.area)}`;
  });

  const totalInclusionPrice = computed(() => {
    return Object.values(form.inclusionPrices).reduce(
      (sum, price) => sum + price,
      0
    );
  });

  const updateFeatures = (feature: HouseFeature) => {
    if (feature.isSelected) {
      form.features.push(feature.name);
    } else {
      form.features = form.features.filter((f) => f !== feature.name);
    }
  };

  const uploadImageRequest = async (formData: FormData) => {
    isUploadingImage.value = true;

    try {
      const res = await store.dispatch("builder/uploadHouseImages", {
        formData,
        builderId: builderId.value,
      });
      const url = res.data[0].value.Location;

      setImagesUrl(imageToUpload.value.type, url, imageToUpload.value.id);
    } catch (error) {
      toast.error("An error occurred while uploading the image");
    } finally {
      isUploadingImage.value = false;
    }
  };

  const validateMultipleImageInput = (
    data: string[],
    type: ImagesHouseTypes,
    url: string
  ) => {
    if (
      data[data.length - 1] === url &&
      validsMultipleImagesInputs.includes(type)
    ) {
      return true;
    }

    return false;
  };

  const addImage = (type: ImagesHouseTypes) => {
    const image = getEmptyImage();

    if (!form[type].some((i) => i.id === image.id)) {
      form[type].push(image);
    }

    const imageToRender = imagesToRender.value.find(
      (image) => image.type === type
    );

    if (imageToRender && !imageToRender.data.some((i) => i.id === image.id)) {
      imageToRender.data.push(image);
    }
  };
  const showUploadButton = (type: ImagesHouseTypes, index: number) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      if (!form.mainImages || form.mainImages.length === 0) {
        return true;
      }

      if (form.mainImages[0]?.url === "") {
        return true;
      }

      const isLastImage = index === form.mainImages.length - 1;

      return isLastImage;
    }

    if (type === ImagesHouseTypes.FLOORPLANS) {
      return !form.floorplans.length || form.floorplans[0]?.url === "";
    }

    return true;
  };

  const setImagesUrl = (type: ImagesHouseTypes, url: string, id: string) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      const emptyImageIndex = form[type].findIndex((img) => !img.url);

      if (emptyImageIndex >= 0) {
        form[type][emptyImageIndex].url = url;
      } else {
        const newId = uuidv4();

        form[type].push({ id: newId, url: url });
      }

      const mainImageToRender = imagesToRender.value.find(
        (image) => image.type === type
      );

      if (mainImageToRender) {
        mainImageToRender.data = form[type];
      }

      return;
    }

    const found = form[type].find((x) => x.id === id);

    if (found) {
      found.id = id;
    }

    const imageToRender = imagesToRender.value
      .find((image) => image.type === type)
      ?.data.find((url) => url.id === id);

    if (imageToRender) {
      imageToRender.url = url;
    }
  };

  const onNameChange = (type: ImagesHouseTypes, id: string, name: string) => {
    form[type].find((el: { id: string }) => el.id === id).name = name;
  };

  const onDescriptionChange = (
    type: ImagesHouseTypes,
    id: string,
    description: string
  ) => {
    form[type].find((el: { id: string }) => el.id === id).description =
      description;
  };

  const onColorChange = (type: ImagesHouseTypes, id: string, color: string) => {
    form[type].find((el: { id: string }) => el.id === id).color = color;
  };
  const onPriceChange = (type: ImagesHouseTypes, id: string, price: string) => {
    form[type].find((el: { id: string }) => el.id === id).price = price;
  };

  const onUploadImage = (
    e: InputEvent,
    type: ImagesHouseTypes,
    id: string
  ): void => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) {
      return;
    }

    imageToUpload.value.id = id;
    imageToUpload.value.type = type;
    imageToUpload.value.data = "";
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      imageToUpload.value.data = reader.result as string;
      isCropperModalOpen.value = true;
    };
  };

  const onRemoveImage = (type: ImagesHouseTypes, id: string) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      form.mainImages = form.mainImages.filter((x) => x.id !== id);

      if (form.mainImages.length === 0) {
        form.mainImages = [{ id: uuidv4(), url: "" }];
      }

      const mainImageToRender = imagesToRender.value.find(
        (image) => image.type === type
      );

      if (mainImageToRender) {
        mainImageToRender.data = form.mainImages;
      }
    } else {
      setImagesUrl(type, "", id);
    }
  };

  const cleanFloorplans = (floorplans: Floorplan[] | string[]) => {
    if (typeof floorplans[0] === "string") {
      return floorplans.map((url) => ({
        url: url as string,
        name: "",
        description: "",
      }));
    }

    return floorplans as Floorplan[];
  };

  const filterDataRemoveImage = (data: string[], url: string) => {
    if (data.length === 1) {
      return [""];
    }

    return data.filter((imageUrl) => imageUrl !== url);
  };

  const formatFormFields = () => {
    const types = Object.values(ImagesHouseTypes);

    const formated = { ...form };

    types.forEach((type) => {
      for (var i = 0; i < formated[type].length; i++) {
        delete formated[type][i].id;
      }
    });

    return {
      ...formated,
      categorizedInclusions: form.categorizedInclusions,
      lounges: form.lounges.map((l) => l.url),
      mainImages: form.mainImages.map((m) => m.url),
      inclusionPack: form.inclusions.map((id) => ({
        inclusionId: inclusions.value.find((inc) => inc._id === id),
        price: form.inclusionPrices[id] || 0,
      })),
      inclusions: undefined,
      inclusionPrices: undefined,
    };
  };

  const updateCategorizedInclusions = (
    category: InclusionCategory,
    items: { text: string }[]
  ) => {
    categorizedInclusionsMap[category] = items
      .map((item) => item.text)
      .filter(Boolean);
    form.categorizedInclusions = inclusionCategories
      .map((entry) => ({
        category: entry.key,
        items: categorizedInclusionsMap[entry.key] || [],
      }))
      .filter((entry) => entry.items.length > 0);
  };

  const validateForm = async () => {
    v$.value.$touch();

    const isValid = await v$.value.$validate();

    const areFacadesErrors = form.facades?.length <= 0;

    if (!isValid) {
      toast.error("Please fill the required fields");
      return false;
    }

    const hasMainImage = form.mainImages.some((img) => img.url);

    if (!hasMainImage) {
      toast.error("Please upload a main image");
      return false;
    }

    if (!form[ImagesHouseTypes.FLOORPLANS][0]?.url) {
      toast.error("Please upload a floorplan image");
      return false;
    }

    if (areFacadesErrors) {
      toast.error("Please select at least one facade");
      return false;
    }

    const hasInvalidInclusionPrices = form.inclusions.some(
      (id) => form.inclusionPrices[id] < 0
    );

    if (hasInvalidInclusionPrices) {
      toast.error("All selected inclusions must have a price greater than 0");
      return false;
    }

    return true;
  };

  const clearForm = () => {
    Object.assign(form, formFields);
    resetVuelidateErrors(v$);
    imagesToRender.value = imagesToRender.value.map((image) => ({
      type: image.type,
      label: image.label,
      data: [],
    }));
  };

  const isFeatureSelected = (feature: string) => {
    return form.features?.includes(feature);
  };

  const onUpdateHouse = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    loading.value = true;
    const formattedForm = formatFormFields();

    try {
      await store.dispatch("builder/updateHouse", {
        ...formattedForm,
        _id: houseId.value,
        builderId: builderId.value,
        loungeRoom: {
          quantity: formattedForm.loungeRoom,
          urls: formattedForm.lounges,
        },
        bathroom: {
          quantity: formattedForm.bathroom,
          options: formattedForm.bathrooms,
        },
      });
      toast.success("House updated successfully");
      router.push({
        name: "AdminBuilder",
        params: { builderId: builderId.value },
      });
    } catch (error) {
      toast.error("An error occurred while updating the house");
    } finally {
      loading.value = false;
    }
  };

  const cleanImagesToRender = (
    house: Record<string, any>,
    type: ImagesHouseTypes
  ) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      if (!house[type] || house[type].length === 0) {
        return [{ id: uuidv4(), url: "" }];
      }

      return house[type];
    }

    switch (type) {
      case ImagesHouseTypes.BATHROOMS:
        return house.bathroom.options;
      case ImagesHouseTypes.LOUNGES_ROOMS:
        return house.loungeRoom.urls;
      default:
        return house[type] || [];
    }
  };

  const fillFormWithHouseData = (house: HouseDesignInterface) => {
    addIdToData(house);
    form.name = house.name;
    form.description = house.description || "";
    form.area = house.area;
    form.length = house.length;
    form.width = house.width;
    form.bathroom = house.bathroom.quantity;
    form.bathrooms = house.bathroom.options;
    form.lounges = house.loungeRoom.urls;

    if (house.mainImages.length === 0) {
      form.mainImages = [{ id: uuidv4(), url: "" }];
    } else {
      form.mainImages = house.mainImages;
    }

    form.facades = house.facades.map((f) => f._id);
    form.internalColorSchemes =
      house.internalColorSchemes?.map((f) => f._id) || [];
    form.optionalUpgrades = house.optionalUpgrades?.map((f) => f._id);
    form.basePrice = house.basePrice;

    if (house.inclusionPack) {
      form.inclusions = house.inclusionPack.map((pack) => pack.inclusionId._id);
      form.inclusionPrices = house.inclusionPack.reduce((acc, pack) => {
        acc[pack.inclusionId._id] = pack.price;
        return acc;
      }, {} as Record<string, number>);
    } else {
      form.inclusions = [];
      form.inclusionPrices = {};
    }

    form.floorplans = cleanFloorplans(house.floorplans);
    form.kitchens = house.kitchens;
    form.features = house.features;

    if (house.categorizedInclusions?.length) {
      house.categorizedInclusions.forEach((section) => {
        categorizedInclusionsMap[section.category] = section.items || [];
      });
      form.categorizedInclusions = house.categorizedInclusions;
    }

    form.bedroom = house.bedroom;
    form.garage = house.garage;
    form.storey = house.storey;
    form.loungeRoom = house.loungeRoom.quantity;
    imagesToRender.value = imagesToRender.value.map((image) => ({
      type: image.type,
      label: image.label,
      data: cleanImagesToRender(house, image.type),
    }));
  };

  const addIdToData = (house: Record<string, string>) => {
    const types = Object.values(ImagesHouseTypes);

    types.forEach((type) => {
      switch (type) {
        case ImagesHouseTypes.BATHROOMS:
          house.bathroom.options.forEach((o) => (o.id = uuidv4()));
          break;
        case ImagesHouseTypes.LOUNGES_ROOMS: {
          const lounges = house.loungeRoom.urls.map((u) => ({
            id: uuidv4(),
            url: u,
          }));

          house.loungeRoom.urls = lounges;
          break;
        }
        case ImagesHouseTypes.MAIN_IMAGE: {
          const images = house.mainImages.map((m) => ({
            id: uuidv4(),
            url: m,
          }));

          house.mainImages = images;
          break;
        }
        default:
          house[type].forEach((x) => (x.id = uuidv4()));
          break;
      }
    });
  };

  const getHouse = async () => {
    loading.value = true;

    try {
      const { data } = await store.dispatch(
        "builder/getHouseById",
        houseId.value
      );

      house.value = data;
      getHouseFeatures(data);
      getHomeDesignUrl(data);
      fillFormWithHouseData({
        ...data,
        lounges: data.loungeRoom,
        bathrooms: data.bathroom,
      });
    } catch (error) {
      toast.error("An error occurred while fetching the house");
    } finally {
      loading.value = false;
    }
  };

  const getHomeDesignUrl = (data: HouseDesignInterface) => {
    const shapesHomesBuilderId =
      process.env.VUE_APP_SHAPEHOMES_SUBDOMAIN_BUILDER_ID;
    const shapesHomesUrl = process.env.VUE_APP_SHAPEHOMES_URL;
    const webUrl = process.env.VUE_APP_WEB_URL;
    const builderId = data.builderId;

    if (data.builderId === shapesHomesBuilderId) {
      homeDesignUrl.value = `${shapesHomesUrl}/builder-estimator/${builderId}?houseId=${data._id}`;
    } else {
      homeDesignUrl.value = `${webUrl}/builder-estimator/${builderId}?houseId=${data._id}`;
    }
  };

  async function getResult(file: File) {
    isCropperModalOpen.value = false;
    imageToUpload.value.data = file;
    const formData = new FormData();

    formData.append("files", imageToUpload.value.data);
    uploadImageRequest(formData);
  }

  const getHouseFeatures = (data: HouseDesignInterface) => {
    addHouseFeatures(data.features);
  };

  const addHouseFeatures = (features: string[]) => {
    houseFeatures.value = features.map((f) => ({
      name: f,
      isSelected: true,
    }));
  };

  onMounted(() => {
    getHouse();
  });

  async function onCropImage(file: File) {
    isCropperModalOpen.value = false;
    imageToUpload.value.data = file;
    const formData = new FormData();

    formData.append("files", file);
    uploadImageRequest(formData);
  }

  const getBuilderFacades = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("facade/getFacadesByQuery", {
        builderId: builderId.value,
      });

      facades.value = data;
    } catch (error) {
      toast.error("An error occurred while getting builder");
    } finally {
      loading.value = false;
    }
  };

  const updateFacades = (facade: FacadeInterface) => {
    const foundFacade = form.facades.includes(facade._id);

    if (!foundFacade) form.facades.push(facade._id);
    else form.facades = form.facades.filter((f) => f !== facade._id);
  };

  const getColorSchemes = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("colorScheme/getColorSchemeByQuery", {
        builderId: builderId.value,
      });

      data.forEach((colorScheme: ColorSchemeInterface) => {
        if (colorScheme.type === ColorSchemeType.INTERNAL) {
          internalColorSchemes.value.push(
            colorScheme as InternalColorSchemeInterface
          );
        }
      });
    } catch (error) {
      toast.error("An error occurred while getting builder");
    } finally {
      loading.value = false;
    }
  };

  const updateInternalColorSchemes = (
    colorScheme: InternalColorSchemeInterface
  ) => {
    const foundInternalColor = form.internalColorSchemes.includes(
      colorScheme._id
    );

    if (!foundInternalColor) form.internalColorSchemes.push(colorScheme._id);
    else
      form.internalColorSchemes = form.internalColorSchemes.filter(
        (f) => f !== colorScheme._id
      );
  };

  const getInclusions = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("inclusion/getInclusionsByQuery", {
        builderId: builderId.value,
      });

      inclusions.value = data;
    } catch (error) {
      toast.error("An error occurred while getting inclusions");
    } finally {
      loading.value = false;
    }
  };

  const updateInclusions = (inclusion: InclusionInterface) => {
    const foundInclusion = form.inclusions.includes(inclusion._id);

    if (!foundInclusion) {
      form.inclusions.push(inclusion._id);
      const inclusionPack = house.value?.inclusionPack?.find(
        (pack) => pack.inclusionId._id === inclusion._id
      );

      form.inclusionPrices[inclusion._id] = inclusionPack?.price || 0;
    } else {
      form.inclusions = form.inclusions.filter((i) => i !== inclusion._id);
      delete form.inclusionPrices[inclusion._id];
    }
  };

  const getOptionalUpgrades = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch(
        "optionalUpgrade/getOptionalUpgradesByBuilderId",
        {
          builderId: builderId.value,
        }
      );

      optionalUpgrades.value = data;
    } catch (error) {
      toast.error("An error occurred while getting optional upgrades");
    } finally {
      loading.value = false;
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(homeDesignUrl.value)
      .then(() => {
        toast.success("URL copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy URL");
      });
  };

  const updateOptionalUpgrades = (optionalUpgrade: OptionalUpgrade) => {
    const foundOptionalUpgrade = form.optionalUpgrades.includes(
      optionalUpgrade._id
    );

    if (!foundOptionalUpgrade) form.optionalUpgrades.push(optionalUpgrade._id);
    else
      form.optionalUpgrades = form.optionalUpgrades.filter(
        (f) => f !== optionalUpgrade._id
      );
  };

  const handleCreateInternalColorScheme = async (
    dto: CreateInternalColorSchemeInterface
  ) => {
    try {
      const created = await store.dispatch(
        "colorScheme/createColorScheme",
        dto
      );

      if (created && created.type === ColorSchemeType.INTERNAL) {
        internalColorSchemes.value.push(
          created as InternalColorSchemeInterface
        );
      }
    } catch (error) {
      toast.error("An error occurred while creating the internal color scheme");
    } finally {
      isInternalSchemeModalOpen.value = false;
    }
  };

  const handleCreateInclusion = async (dto: CreateInclusionInterface) => {
    try {
      const created = await store.dispatch("inclusion/createInclusion", dto);

      if (created) {
        inclusions.value.push(created as InclusionInterface);
      }
    } catch (error) {
      toast.error("An error occurred while creating the inclusion");
    } finally {
      isInclusionModalOpen.value = false;
    }
  };

  const handleCreateOptionalUpgrade = async (
    dto: CreateOptionalUpgradeInterface
  ) => {
    try {
      const created = await store.dispatch(
        "optionalUpgrade/createOptionalUpgrade",
        dto
      );

      if (created) {
        optionalUpgrades.value.push(created as OptionalUpgrade);
      }
    } catch (error) {
      toast.error("An error occurred while creating the optional upgrade");
    } finally {
      isOptionalUpgradeModalOpen.value = false;
    }
  };

  const addNewFeature = () => {
    if (!newFeature.value.trim()) return;

    const feature = {
      name: newFeature.value,
      isSelected: true,
    };

    if (!houseFeatures.value.some((f) => f.name === feature.name)) {
      houseFeatures.value.push(feature);
      form.features.push(feature.name);
    }

    newFeature.value = "";
  };

  const removeFeature = (index: number) => {
    const featureName = houseFeatures.value[index].name;

    houseFeatures.value.splice(index, 1);
    form.features = form.features.filter((f) => f !== featureName);
  };

  const updateFeatureText = (index: number, name: string) => {
    const oldName = houseFeatures.value[index].name;
    const featureIndex = form.features.findIndex((f) => f === oldName);

    if (featureIndex !== -1) {
      form.features[featureIndex] = name;
    }
  };

  watch(
    builderId,
    (id) => {
      if (!id) {
        return;
      }

      getBuilderFacades();
      getColorSchemes();
      getInclusions();
      getOptionalUpgrades();
    },
    { immediate: true }
  );

  watch(
    () => form.storey,
    () => {
      changesStorey.value = changesStorey.value + 1;

      if (changesStorey.value > 1) {
        form.facades = [];
      }
    }
  );

  watch(
    () => form.mainImages,
    () => {
      console.log(form.mainImages);
      console.log(imagesToRender.value);
    }
  );

const __returned__ = { houseId, imageToUpload, isCropperModalOpen, toast, filteredFacades, facades, changesStorey, internalColorSchemes, optionalUpgrades, loading, isUploadingImage, isUploadingInclusion, inclusionName, inclusions, house, houseFeatures, newFeature, homeDesignUrl, isInternalSchemeModalOpen, isInclusionModalOpen, isOptionalUpgradeModalOpen, getEmptyImage, imagesToRender, formFields, form, inclusionCategories, categorizedInclusionsMap, rules, v$, builderId, perSQM, totalInclusionPrice, updateFeatures, uploadImageRequest, validateMultipleImageInput, addImage, showUploadButton, setImagesUrl, onNameChange, onDescriptionChange, onColorChange, onPriceChange, onUploadImage, onRemoveImage, cleanFloorplans, filterDataRemoveImage, formatFormFields, updateCategorizedInclusions, validateForm, clearForm, isFeatureSelected, onUpdateHouse, cleanImagesToRender, fillFormWithHouseData, addIdToData, getHouse, getHomeDesignUrl, getResult, getHouseFeatures, addHouseFeatures, onCropImage, getBuilderFacades, updateFacades, getColorSchemes, updateInternalColorSchemes, getInclusions, updateInclusions, getOptionalUpgrades, copyToClipboard, updateOptionalUpgrades, handleCreateInternalColorScheme, handleCreateInclusion, handleCreateOptionalUpgrade, addNewFeature, removeFeature, updateFeatureText, ref, reactive, computed, onMounted, watch, get store() { return store }, get router() { return router }, get ImagesHouseTypes() { return ImagesHouseTypes }, get ImageHouseLabels() { return ImageHouseLabels }, get Floorplan() { return Floorplan }, get Inclusions() { return Inclusions }, get HouseFeature() { return HouseFeature }, get HouseDesignInterface() { return HouseDesignInterface }, get ImageToRender() { return ImageToRender }, get useToast() { return useToast }, get required() { return required }, get minValue() { return minValue }, get maxValue() { return maxValue }, get resetVuelidateErrors() { return resetVuelidateErrors }, get useVuelidate() { return useVuelidate }, get VuePictureCropper() { return VuePictureCropper }, get cropper() { return cropper }, ImageCropperModal, get ImageToUpload() { return ImageToUpload }, get validImagesColorInputs() { return validImagesColorInputs }, get validsImagesNameInputs() { return validsImagesNameInputs }, get validImagesDescriptionInputs() { return validImagesDescriptionInputs }, get validsMultipleImagesInputs() { return validsMultipleImagesInputs }, ListItemGenerator, get formatPrice() { return formatPrice }, get formatPriceTrim() { return formatPriceTrim }, get updatePriceInput() { return updatePriceInput }, CheckboxInput, get uuidv4() { return uuidv4 }, get FacadeInterface() { return FacadeInterface }, get ColorSchemeInterface() { return ColorSchemeInterface }, get CreateInternalColorSchemeInterface() { return CreateInternalColorSchemeInterface }, get InternalColorSchemeInterface() { return InternalColorSchemeInterface }, get ColorSchemeType() { return ColorSchemeType }, get CreateInclusionInterface() { return CreateInclusionInterface }, get InclusionInterface() { return InclusionInterface }, get CreateOptionalUpgradeInterface() { return CreateOptionalUpgradeInterface }, get OptionalUpgrade() { return OptionalUpgrade }, get InclusionCategory() { return InclusionCategory }, get AssetGalleryFormType() { return AssetGalleryFormType }, AddAndUpdateAssetInGalleryModal, AddAndUpdateInclusionModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})